import React, { useCallback, useEffect, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import Dropdown from "../../common/dropDown";
import ConfirmModal from "../../common/ConfirmModal";
import DocumentPreviewModal from "../../common/DocumentPreview/DocumentPreviewModal";
import ReportTable from "../../common/ReportTable/ReportTable";
import FileUpload from "../../common/fileupload/FileUpload";
import "../../common/fileupload/FileUpload.css";
import {
  STAFF_DOCUMENT_TYPES,
  STAFF_DOCUMENT_EXPIRY_SUFFIXES,
  STAFF_PROFILE_DOCUMENT_ALLOWED_EXTENSIONS,
  STAFF_PROFILE_DOCUMENT_MAX_FILE_SIZE_MB,
  buildStaffDocumentDownloadUrl,
  fetchStaffDocuments,
  formatCertificationDateForApi,
  getFileExtension,
  getStaffProfileDocumentFileError,
  readFileAsBase64,
  saveStaffDocument,
  setStaffDocumentActiveState,
} from "../../../services/staffDocumentService";
import "./UserProfileStaffDocuments.scss";

const EMPTY_FORM = {
  displayName: "",
  certificateType: null,
  certificationDate: null,
  expiryNumber: "",
  expirySuffix: null,
  studyId: null,
};

const DOCUMENT_FIELD_OPTIONS = [
  { key: "DocumentName", label: "Document name" },
  { key: "CertificateType", label: "Type" },
  { key: "CertificateDate", label: "Certification date" },
  { key: "ExpiryDate", label: "Expiry date" },
  { key: "IsActive", label: "Status" },
  { key: "UploadedDateTime", label: "Uploaded" },
  { key: "UploadedBy", label: "Uploaded by" },
];

const DEFAULT_DOCUMENT_COLUMNS = [
  "DocumentName",
  "CertificateType",
  "CertificateDate",
  "ExpiryDate",
  "IsActive",
  "UploadedDateTime",
  "UploadedBy",
];

function normalizeDocumentRow(row, index) {
  const fileName = row.DocumentName || row.documentName || "—";
  const isActive = String(row.IsActive ?? row.isActive ?? "Yes");
  const expired = String(row.Expired ?? row.expired ?? "0") === "1";

  return {
    id: row.DocumentId || row.documentId || `doc-${index}`,
    DocumentName: fileName,
    CertificateType: row.CertificateType || row.certificateType || "—",
    CertificateDate: row.CertificateDate || row.certificateDate || "—",
    ExpiryDate: row.ExpiryDate || row.expiryDate || "N/A",
    IsActive: isActive,
    UploadedDateTime: row.UploadedDateTime || row.uploadedDateTime || "—",
    UploadedBy: row.UploadedBy || row.uploadedBy || "—",
    Expired: expired,
    IsLocked: !!(row.IsLocked ?? row.isLocked),
    LockedReason: row.LockedReason || row.lockedReason || "",
    _raw: row,
  };
}

function UserProfileStaffDocuments({
  staffId,
  organizationId,
  createdBy,
  studyOptions = [],
  canEdit = false,
  showAlert,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadPickerKey, setUploadPickerKey] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [preview, setPreview] = useState(null);
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    action: null,
    row: null,
  });
  const [statusUpdating, setStatusUpdating] = useState(false);

  const documentTypeOptions = useMemo(
    () => STAFF_DOCUMENT_TYPES.map((label) => ({ label, value: label })),
    [],
  );

  const expirySuffixOptions = useMemo(
    () =>
      STAFF_DOCUMENT_EXPIRY_SUFFIXES.map((label) => ({ label, value: label })),
    [],
  );

  const isStudyTraining =
    form.certificateType?.value === "Study Training" ||
    form.certificateType?.label === "Study Training";

  const tableRows = useMemo(
    () => rows.map((row, index) => normalizeDocumentRow(row, index)),
    [rows],
  );

  const loadDocuments = useCallback(async () => {
    if (!organizationId || !staffId || !createdBy) return;
    setLoading(true);
    try {
      const list = await fetchStaffDocuments({
        organizationId,
        staffId,
        createdBy,
      });
      setRows(Array.isArray(list) ? list : []);
    } catch (error) {
      setRows([]);
      showAlert?.({
        type: "error",
        message: error?.message || "Could not load documents.",
      });
    } finally {
      setLoading(false);
    }
  }, [organizationId, staffId, createdBy, showAlert]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const resetUploadForm = () => {
    setSelectedFile(null);
    setFileError("");
    setForm(EMPTY_FORM);
    setUploadPickerKey((n) => n + 1);
  };

  const openUploadModal = () => {
    resetUploadForm();
    setUploadModalOpen(true);
  };

  const closeUploadModal = () => {
    if (saving) return;
    setUploadModalOpen(false);
    resetUploadForm();
  };

  const applySelectedUploadFile = useCallback(
    (file) => {
      if (!file) {
        setSelectedFile(null);
        return;
      }
      const validationError = getStaffProfileDocumentFileError(file);
      if (validationError) {
        setSelectedFile(null);
        setFileError(validationError);
        setUploadPickerKey((n) => n + 1);
        return;
      }
      setFileError("");
      setSelectedFile(file);
      setForm((prev) => {
        if (prev.displayName) return prev;
        const base = String(file.name || "").replace(/\.[^.]+$/, "");
        return { ...prev, displayName: base };
      });
    },
    [],
  );

  const handleUploadFileSelect = useCallback(
    (result) => {
      const file = result?.file || (result instanceof File ? result : null);
      applySelectedUploadFile(file);
    },
    [applySelectedUploadFile],
  );

  const handleUploadFileError = useCallback((error) => {
    const message =
      error?.message ||
      `Only PDF and image files are allowed (${STAFF_PROFILE_DOCUMENT_ALLOWED_EXTENSIONS.join(
        ", ",
      )}). Max ${STAFF_PROFILE_DOCUMENT_MAX_FILE_SIZE_MB} MB.`;
    setFileError(message);
    setSelectedFile(null);
  }, []);

  const handleSave = async () => {
    if (!canEdit) return;
    if (!selectedFile) {
      setFileError("Please choose a file to upload.");
      showAlert?.({ type: "warning", message: "Please choose a file to upload." });
      return;
    }
    const certType = form.certificateType?.value || form.certificateType?.label;
    if (!certType) {
      showAlert?.({ type: "warning", message: "Please select a document type." });
      return;
    }
    if (!form.certificationDate) {
      showAlert?.({
        type: "warning",
        message: "Please enter the date of certification.",
      });
      return;
    }
    if (isStudyTraining && !(form.studyId?.value || form.studyId)) {
      showAlert?.({ type: "warning", message: "Please select a study." });
      return;
    }

    const validationError = getStaffProfileDocumentFileError(selectedFile);
    if (validationError) {
      setFileError(validationError);
      showAlert?.({ type: "warning", message: validationError });
      return;
    }

    setSaving(true);
    try {
      const base64 = await readFileAsBase64(selectedFile);
      const ext = getFileExtension(selectedFile.name);
      const result = await saveStaffDocument({
        organizationId,
        staffId,
        createdBy,
        fileBase64: base64,
        fileName: selectedFile.name,
        displayName: form.displayName?.trim() || "",
        certificateType: certType,
        certificationDate: formatCertificationDateForApi(form.certificationDate),
        expiryNumber: form.expiryNumber?.trim() || "",
        expirySuffix: form.expirySuffix?.value || form.expirySuffix?.label || "",
        studyId: form.studyId?.value || form.studyId || "",
        documentSizeKb: (selectedFile.size / 1024).toFixed(2),
        fileExtension: ext,
      });

      showAlert?.({
        type: "success",
        message: result?.message || "Document saved successfully.",
      });
      setUploadModalOpen(false);
      resetUploadForm();
      await loadDocuments();
    } catch (error) {
      showAlert?.({
        type: "error",
        message: error?.message || "Could not save document.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActiveClick = useCallback((row) => {
    if (!canEdit || row?.IsLocked) return;
    const isActive = String(row.IsActive ?? "Yes") === "Yes";
    setConfirmModal({
      open: true,
      action: isActive ? "deactivate" : "activate",
      row,
    });
  }, [canEdit]);

  const handleConfirmClose = useCallback(() => {
    if (statusUpdating) return;
    setConfirmModal({ open: false, action: null, row: null });
  }, [statusUpdating]);

  const handleConfirmToggleActive = useCallback(async () => {
    const { row, action } = confirmModal;
    if (!canEdit || !row || !action || statusUpdating) return;

    const raw = row._raw || row;
    const documentId = raw.DocumentId || raw.documentId;
    if (!documentId) return;

    try {
      setStatusUpdating(true);
      const result = await setStaffDocumentActiveState({
        organizationId,
        staffId,
        createdBy,
        documentId,
        isActive: action === "activate",
      });
      showAlert?.({
        type: "success",
        message: result?.message || "Document updated.",
      });
      setConfirmModal({ open: false, action: null, row: null });
      await loadDocuments();
    } catch (error) {
      showAlert?.({
        type: "error",
        message: error?.message || "Could not update document status.",
      });
    } finally {
      setStatusUpdating(false);
    }
  }, [
    canEdit,
    confirmModal,
    createdBy,
    loadDocuments,
    organizationId,
    showAlert,
    staffId,
    statusUpdating,
  ]);

  const openPreview = (row) => {
    const raw = row._raw || row;
    const url = buildStaffDocumentDownloadUrl(raw.DocumentURL || raw.documentURL);
    if (!url || /data:/i.test(String(url))) {
      showAlert?.({
        type: "warning",
        message: "Preview is not available for this file. Please re-upload it.",
      });
      return;
    }
    const name = raw.DocumentName || raw.documentName || row.DocumentName || "Document";
    setPreview({
      documentId: raw.DocumentId || raw.documentId || row.id || "",
      documentUrl: url,
      documentExtension: getFileExtension(name),
      documentTitle: name,
      documentCode: raw.CertificateType || raw.certificateType || "",
    });
  };

  const getFieldLabel = useCallback((field) => {
    return DOCUMENT_FIELD_OPTIONS.find((opt) => opt.key === field)?.label || field;
  }, []);

  const renderReportCell = useCallback(
    (row, field) => {
      if (field === "DocumentName") {
        return (
          <button
            type="button"
            className={`report-table__cell-link staff-documents-name-link${
              row.Expired ? " staff-documents-cell--expired" : ""
            }`}
            onClick={() => openPreview(row)}
            title={`Open / download ${row.DocumentName}`}
          >
            {row.DocumentName}
            {row.IsLocked && (
              <i
                className="fas fa-lock text-danger ms-2"
                title={row.LockedReason || "Locked"}
                aria-hidden="true"
              />
            )}
          </button>
        );
      }

      if (field === "IsActive") {
        const active = String(row.IsActive) === "Yes";
        const canToggle = canEdit && !row.IsLocked;
        if (!canToggle) {
          return (
            <span
              className={`staff-documents-status-btn is-${
                active ? "active" : "inactive"
              } is-readonly`}
            >
              {active ? "Active" : "Inactive"}
            </span>
          );
        }
        return (
          <button
            type="button"
            className={`staff-documents-status-btn is-${
              active ? "active" : "inactive"
            }`}
            onClick={() => handleToggleActiveClick(row)}
            title={active ? "Make inactive" : "Make active"}
            aria-label={active ? "Make inactive" : "Make active"}
          >
            <span>{active ? "Active" : "Inactive"}</span>
            <i className="fas fa-pencil-alt" aria-hidden="true" />
          </button>
        );
      }

      return String(row[field] ?? "—");
    },
    [canEdit, handleToggleActiveClick, openPreview],
  );

  const renderUploadForm = () => (
    <div className="row g-3">
      <div className="col-12">
        <label className="form-label">
          File <span className="mandatory">*</span>
        </label>
        <div
          className={`staff-documents-file-picker${
            fileError ? " has-error" : ""
          }`}
        >
          <FileUpload
            key={uploadPickerKey}
            acceptedFormats={[...STAFF_PROFILE_DOCUMENT_ALLOWED_EXTENSIONS]}
            maxFileSize={STAFF_PROFILE_DOCUMENT_MAX_FILE_SIZE_MB}
            multiple={false}
            returnType="file"
            showPreview={false}
            showFileList={false}
            autoUpload
            disabled={saving}
            onFileSelect={handleUploadFileSelect}
            onError={handleUploadFileError}
            className="staff-documents-file-upload"
          />
          {fileError ? (
            <p className="staff-documents-file-error" role="alert">
              {fileError}
            </p>
          ) : (
            <p className="staff-documents-file-hint">
              PDF or image only (pdf, png, jpg, jpeg, gif, webp). Max{" "}
              {STAFF_PROFILE_DOCUMENT_MAX_FILE_SIZE_MB} MB.
            </p>
          )}
        </div>
      </div>

      <div className="col-md-6">
        <label className="form-label">Display name</label>
        <input
          type="text"
          className="form-control"
          value={form.displayName}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, displayName: e.target.value }))
          }
          placeholder="Optional"
        />
      </div>

      <div className="col-md-6">
        <label className="form-label">
          Document type <span className="mandatory">*</span>
        </label>
        <Dropdown
          options={documentTypeOptions}
          isMulti={false}
          selected={form.certificateType}
          onSelect={(value) =>
            setForm((prev) => ({
              ...prev,
              certificateType: value,
              studyId: null,
            }))
          }
        />
      </div>

      {isStudyTraining && (
        <div className="col-12">
          <label className="form-label">
            Study <span className="mandatory">*</span>
          </label>
          <Dropdown
            options={studyOptions}
            isMulti={false}
            selected={form.studyId}
            onSelect={(value) =>
              setForm((prev) => ({ ...prev, studyId: value }))
            }
          />
        </div>
      )}

      <div className="col-md-6">
        <label className="form-label">
          Date of certification <span className="mandatory">*</span>
        </label>
        <DatePicker
          selected={form.certificationDate}
          onChange={(date) =>
            setForm((prev) => ({ ...prev, certificationDate: date }))
          }
          placeholderText="Select date"
          className="form-control"
          dateFormat="dd/MM/yyyy"
          isClearable
          showMonthDropdown
          showYearDropdown
          dropdownMode="select"
          maxDate={new Date()}
          onChangeRaw={(e) => e.preventDefault()}
        />
      </div>

      <div className="col-md-3">
        <label className="form-label">Expiry period</label>
        <input
          type="text"
          className="form-control text-center"
          maxLength={4}
          value={form.expiryNumber}
          onChange={(e) =>
            setForm((prev) => ({
              ...prev,
              expiryNumber: e.target.value.replace(/\D/g, ""),
            }))
          }
          placeholder="e.g. 12"
        />
      </div>

      <div className="col-md-3">
        <label className="form-label">Period unit</label>
        <Dropdown
          options={expirySuffixOptions}
          isMulti={false}
          selected={form.expirySuffix}
          onSelect={(value) =>
            setForm((prev) => ({ ...prev, expirySuffix: value }))
          }
        />
      </div>
    </div>
  );

  return (
    <>
      <div className="profile-staff-documents">
        <div className="profile-staff-documents__header">
          <div className="profile-staff-documents__header-left">
            <h6 className="profile-staff-documents__title">My Documents</h6>
          </div>
          <div className="profile-staff-documents__toolbar">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={loadDocuments}
              disabled={loading}
            >
              <i className="fas fa-sync-alt me-1" aria-hidden="true" />
              Refresh
            </button>
            {canEdit && (
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={openUploadModal}
              >
                <i className="fas fa-cloud-upload-alt me-1" aria-hidden="true" />
                Upload document
              </button>
            )}
          </div>
        </div>

        <div className="profile-staff-documents__report-wrap">
          <ReportTable
            rows={tableRows}
            fieldOptions={DOCUMENT_FIELD_OPTIONS}
            defaultColumns={DEFAULT_DOCUMENT_COLUMNS}
            getFieldLabel={getFieldLabel}
            renderCell={renderReportCell}
            pageSizeOptions={[10, 25, 50]}
            columnSearchMode="client"
            persistLayoutKey={`user-profile-staff-documents-v3-${staffId}`}
              loading={loading}
              emptyMessage="No documents yet"
              emptyDescription="Upload CV, GCP, or training records using Upload document."
          />
        </div>
      </div>

      <DocumentPreviewModal
        open={Boolean(preview)}
        onClose={() => setPreview(null)}
        sourceModule="STAFF"
        sourceTable="StaffDocumentRecord"
        documentId={preview?.documentId || ""}
        documentUrl={preview?.documentUrl || ""}
        documentExtension={preview?.documentExtension || ""}
        documentTitle={preview?.documentTitle || "Document"}
        documentCode={preview?.documentCode || ""}
      />

      <ConfirmModal
        isOpen={confirmModal.open}
        onClose={handleConfirmClose}
        onConfirm={handleConfirmToggleActive}
        title={
          confirmModal.action === "deactivate"
            ? "Deactivate document"
            : "Activate document"
        }
        message={
          confirmModal.row
            ? `Do you want to ${
                confirmModal.action === "deactivate" ? "deactivate" : "activate"
              } "${confirmModal.row.DocumentName || "this document"}"?`
            : ""
        }
        confirmText={statusUpdating ? "Updating…" : "Yes"}
        cancelText="No"
        confirmButtonClass={
          confirmModal.action === "deactivate" ? "btn-danger" : "btn-success"
        }
      />

      {uploadModalOpen && (
        <div
          className="modal fade show d-block staff-documents-modal-overlay"
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          aria-label="Upload document"
          onClick={(e) =>
            e.target === e.currentTarget && closeUploadModal()
          }
        >
          <div
            className="modal-dialog modal-dialog-centered modal-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content staff-documents-modal">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-cloud-upload-alt me-2" aria-hidden="true" />
                  Upload document
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeUploadModal}
                  disabled={saving}
                  aria-label="Close"
                />
              </div>
              <div className="modal-body">{renderUploadForm()}</div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={closeUploadModal}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <i
                        className="fas fa-spinner fa-spin me-2"
                        aria-hidden="true"
                      />
                      Uploading…
                    </>
                  ) : (
                    <>
                      <i className="fas fa-check me-2" aria-hidden="true" />
                      Upload
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default UserProfileStaffDocuments;
